import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { ArrowLeft, Calendar, Clock, CheckCircle, XCircle, AlertCircle, ExternalLink, MessageCircleQuestion } from 'lucide-react';
import { toast } from 'sonner';
import PreguntasModal from '../components/PreguntasModal';

export default function MisPostulaciones() {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [postulaciones, setPostulaciones] = useState([]);
  const [loading, setLoading] = useState(true);
  const [preguntasFor, setPreguntasFor] = useState(null);

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    fetchPostulaciones();
  }, []);

  const fetchPostulaciones = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_URL}/api/postulaciones/mis-postulaciones`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error('Error al cargar postulaciones');
      }

      const data = await response.json();
      setPostulaciones(data);
    } catch (error) {
      toast.error('Error al cargar las postulaciones');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const getEstadoInfo = (estado) => {
    switch (estado) {
      case 'pendiente':
        return {
          label: 'Pendiente',
          color: 'bg-yellow-100 text-yellow-800',
          icon: Clock
        };
      case 'aprobada':
        return {
          label: 'Aprobada',
          color: 'bg-green-100 text-green-800',
          icon: CheckCircle
        };
      case 'rechazada':
        return {
          label: 'Rechazada',
          color: 'bg-red-100 text-red-800',
          icon: XCircle
        };
      case 'en_revision':
        return {
          label: 'En Revisión',
          color: 'bg-blue-100 text-blue-800',
          icon: AlertCircle
        };
      default:
        return {
          label: estado,
          color: 'bg-gray-100 text-gray-800',
          icon: Clock
        };
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString('es-CO', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return dateStr;
    }
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      {/* Header */}
      <header className="bg-[#0F172A] border-b border-slate-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <img 
              src="https://customer-assets.emergentagent.com/job_login-verify-system/artifacts/gyieqwft_Captura%20de%20Pantalla%202025-12-10%20a%20la%28s%29%207.03.41%20a.%C2%A0m..png" 
              alt="Logo" 
              className="h-12 w-auto object-contain brightness-0 invert"
            />
            <div className="border-l border-slate-600 pl-4">
              <h1 className="text-xl font-semibold text-white">
                Mis Postulaciones
              </h1>
            </div>
          </div>
          <button
            onClick={() => navigate('/dashboard')}
            className="flex items-center gap-2 text-slate-300 hover:text-white transition-colors"
          >
            <ArrowLeft className="h-5 w-5" />
            <span>Volver al Panel</span>
          </button>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Title */}
        <div className="mb-8">
          <h2 className="text-2xl font-bold text-slate-900 mb-2">
            Historial de Postulaciones
          </h2>
          <p className="text-slate-600">
            Revisa el estado de todas tus postulaciones
          </p>
        </div>

        {/* Loading State */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="text-lg text-slate-500">Cargando postulaciones...</div>
          </div>
        ) : postulaciones.length === 0 ? (
          <div className="bg-white rounded-lg p-12 text-center border border-slate-200">
            <p className="text-slate-500 text-lg">No tienes postulaciones aún</p>
            <p className="text-slate-400 mt-2">Explora las convocatorias disponibles y postúlate</p>
            <button
              onClick={() => navigate('/convocatorias-disponibles')}
              className="mt-4 px-6 py-2 bg-[#0F172A] text-white rounded-lg hover:bg-slate-800 transition-colors"
            >
              Ver Convocatorias
            </button>
          </div>
        ) : (
          <div className="space-y-4">
            {postulaciones.map((postulacion) => {
              const estadoInfo = getEstadoInfo(postulacion.estado);
              const EstadoIcon = estadoInfo.icon;
              
              return (
                <div 
                  key={postulacion.id}
                  className="bg-white rounded-lg shadow-sm border border-slate-200 p-6"
                >
                  <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <div className="flex-1">
                      <h3 className="text-lg font-bold text-slate-900 mb-1">
                        {postulacion.convocatoria_nombre}
                      </h3>
                      <p className="text-sm text-slate-500 mb-2">
                        Código: {postulacion.convocatoria_codigo}
                      </p>
                      <div className="flex items-center gap-4 text-sm text-slate-600">
                        <div className="flex items-center gap-1">
                          <Calendar className="h-4 w-4" />
                          <span>Postulado: {formatDate(postulacion.fecha_postulacion)}</span>
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className={`inline-flex items-center gap-1 px-3 py-2 text-sm font-semibold rounded-lg ${estadoInfo.color}`}>
                        <EstadoIcon className="h-4 w-4" />
                        {estadoInfo.label}
                      </span>
                    </div>
                  </div>
                  
                  {postulacion.observaciones && (
                    <div className="mt-4 p-3 bg-slate-50 rounded-lg">
                      <p className="text-sm text-slate-600">
                        <span className="font-medium">Observaciones:</span> {postulacion.observaciones}
                      </p>
                    </div>
                  )}
                  
                  {postulacion.es_externa && (
                    <div className="mt-4 p-3 bg-orange-50 border border-orange-200 rounded-lg">
                      <div className="flex items-center gap-2 text-orange-700">
                        <ExternalLink className="h-4 w-4" />
                        <span className="font-medium text-sm">Postulacion Externa</span>
                      </div>
                      <p className="text-sm text-orange-600 mt-1">
                        Esta postulacion requiere completar un formulario externo.
                      </p>
                      {postulacion.enlace_externo && (
                        <a 
                          href={postulacion.enlace_externo} 
                          target="_blank" 
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 mt-2 text-sm text-blue-600 hover:underline"
                        >
                          <ExternalLink className="h-3 w-3" />
                          Ir al formulario externo
                        </a>
                      )}
                    </div>
                  )}

                  {postulacion.tipo_convocatoria === 'especial' && (
                    <div className="mt-4 flex items-center justify-between gap-3 flex-wrap p-3 bg-amber-50 border border-amber-200 rounded-lg">
                      <div className="flex items-center gap-2 text-amber-800">
                        <MessageCircleQuestion className="h-4 w-4" />
                        <span className="text-sm font-medium">
                          Convocatoria especial — puedes enviar preguntas al funcionario
                        </span>
                      </div>
                      <button
                        onClick={() => setPreguntasFor(postulacion)}
                        className="inline-flex items-center gap-2 px-3 py-1.5 bg-amber-600 text-white rounded-lg hover:bg-amber-700 text-sm font-medium"
                        data-testid={`open-preguntas-${postulacion.id}`}
                      >
                        <MessageCircleQuestion className="h-4 w-4" />
                        Enviar preguntas
                      </button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </main>

      {preguntasFor && (
        <PreguntasModal
          postulacion={preguntasFor}
          onClose={() => setPreguntasFor(null)}
        />
      )}
    </div>
  );
}
